import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { formatTitle } from '../../../utils/utility';
import SettingsSideBar from '../settings/components/settingsSideBar';
import SaleByCustomers from './customers/saleByCustomers';
import './reports.scss';
import favIcon from '../../../assets/image/fav-report-icon.svg';
import favActiveIcon from '../../../assets/image/fav-report-icon-white.svg';
import homeIcon from '../../../assets/image/home-report-icon.svg';
import homeActiveIcon from '../../../assets/image/home-report-icon-white.svg';
import scheduledIcon from '../../../assets/image/scheduled-report-icon.svg';
import scheduledActiveIcon from '../../../assets/image/scheduled-report-icon-white.svg';

import SalesByProducts from './products/salesByProducts';
import FavouriteReports from './favouriteReports';
import ScheduledReports from './scheduledReports';

export default function Reports() {
	const navigate = useNavigate();
	const suburl = localStorage.getItem('suburl');
	const param = useParams();
	const type = param?.type;
	const user = useSelector((state) => state?.userInfo?.userInfo?.data);
	const [activeTab, setActiveTab] = useState('');
	const [activeType, setActiveType] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [isCollapse, setIsCollapse] = useState(false);
	const sidebarData = [
		{
			icon: homeIcon,
			activeIcon: homeActiveIcon,
			title: 'Home',
			items: [],
		},
		{
			icon: favIcon,
			activeIcon: favActiveIcon,
			title: 'Favourite Report',
			items: [],
		},
		{
			icon: scheduledIcon,
			activeIcon: scheduledActiveIcon,
			title: 'Scheduled Report',
			items: [],
		},
		{
			title: 'Customers',
			items: [
				{ label: 'Sale By Customers', permission: 'reports.customers.view' },
				{ label: 'Sale By Products', permission: 'reports.customers.view' },
				{ label: 'Sale By Sales Person', permission: 'reports.customers.view' },
				{ label: 'Sale Return History', permission: 'reports.customers.view' },
			],
		},
		{
			title: 'Inventory',
			items: [
				{ label: 'Inventory Summary', permission: 'reports.inventory.view' },
				{ label: 'Inventory Valuation', permission: 'reports.inventory.view' },
				{ label: 'Product Sale Report', permission: 'reports.inventory.view' },
				{ label: 'Stock Summary', permission: 'reports.inventory.view' },
			],
		},
		{
			title: 'Receivables',
			items: [
				{ label: 'Customer Balances', permission: 'reports.receivables.view' },
				{ label: 'Invoice Reports', permission: 'reports.receivables.view' },
				{ label: 'Receivables Summary', permission: 'reports.receivables.view' },
			],
		},
		{
			title: 'Payments Received',
			items: [
				{ label: 'Payments Received Summary', permission: 'reports.payments_received.view' },
				{ label: 'Refund History', permission: 'reports.payments_received.view' },
			],
		},
		{
			title: 'Payables',
			items: [
				{ label: 'Vendor Balances', permission: 'reports.payables.view' },
				{ label: 'Vendor Credit Reports', permission: 'reports.payables.view' },
				{ label: 'Purchase Bill Reports', permission: 'reports.payables.view' },
				{ label: 'Purchase Order Reports', permission: 'reports.payables.view' },
				{ label: 'Payments Made Reports', permission: 'reports.payables.view' },
				{ label: 'Purchase Order By Vendor', permission: 'reports.payables.view' },
				{ label: 'Payables Summary', permission: 'reports.payables.view' },
			],
		},
		{
			title: 'Purchase/Expense',
			items: [
				{ label: 'Product Wise Purchase', permission: 'reports.product_wise_purchase.view' },
				{ label: 'Invoice Reports', permission: 'reports.invoice_reports.view' },
				{ label: 'Product Receive History', permission: 'reports.product_receive_history.view' },
				{ label: 'Expense Report', permission: 'settings.expense_report.view' },
			],
		},
		{
			title: 'Activity',
			items: [{ label: 'Activity Logs', permission: 'reports.activity_logs.view' }],
		},
	];

	/* ================== HELPERS ================== */
	const labelToType = (label) => label.replaceAll(' ', '-').toLowerCase();

	const filteredSidebarData = sidebarData
		.map((section) => {
			const searchedItems = section.items.filter((item) =>
				item.label.toLowerCase().includes(searchTerm.toLowerCase()),
			);

			return { ...section, items: searchedItems };
		})
		.filter((section, index) => sidebarData[index].items.length === 0 || section.items.length > 0);

	/* ================== AUTO SELECT FIRST PERMITTED PAGE ================== */
	useEffect(() => {
		if (!filteredSidebarData.length) return;

		const isActiveAllowed = filteredSidebarData.some((section) =>
			section.items.some((item) => labelToType(item.label) === type),
		);

		if (!isActiveAllowed) {
			const firstItem =
				filteredSidebarData[0].items.length > 0 ? filteredSidebarData[0].items[0] : [];
			const firstType = labelToType(
				firstItem.length > 0 ? firstItem.label : filteredSidebarData[0].title,
			);
			setActiveTab(firstItem.length > 0 ? firstItem.label : filteredSidebarData[0].title);
			setActiveType(firstItem.length > 0 ? firstItem.label : filteredSidebarData[0].title);
			navigate(`/user/${suburl}/reports/${firstType}`);
		}
	}, []);

	useEffect(() => {
		if (type) {
			setActiveType(type);
			setActiveTab(formatTitle(type));
		}
	}, [type]);

	const componentMap = {
		'sale-by-customers': SaleByCustomers,
		'sale-by-products': SalesByProducts,
		'favourite-report': FavouriteReports,
		'scheduled-report': ScheduledReports,
	};

	const ActiveComponent = componentMap[activeType];

	/* ================== RENDER ================== */
	if (!filteredSidebarData.length && (searchTerm === '' || searchTerm === undefined)) {
		return (
			<div>
				<div style={{ backgroundColor: '#EDEDF7', position: 'sticky', top: 0 }}>
					<div className="fs-4 fw-semibold py-4 px-2">Reports</div>
				</div>
				<div
					className="d-flex justify-content-center align-items-center"
					style={{ marginTop: '250px' }}
				>
					<div className="fs-4 fw-semibold">
						Access denied. You don't have access to view Settings
					</div>
				</div>
			</div>
		);
	}

	/* ================== URL SYNC ================== */

	return (
		<div>
			{!isCollapse && (
				<SettingsSideBar
					activeTab={activeTab}
					setActiveTab={setActiveTab}
					setSearchTerm={setSearchTerm}
					searchTerm={searchTerm}
					filteredSidebarData={filteredSidebarData}
					sideBarType={'reports'}
				/>
			)}

			<div className={`${isCollapse ? 'collapse-reports-section' : 'expand-reports-section'} p-8 `}>
				{ActiveComponent ? (
					<ActiveComponent
						activeTab={activeTab}
						setIsCollapse={setIsCollapse}
						isCollapse={isCollapse}
					/>
				) : null}
			</div>
		</div>
	);
}
